// Was das CMS zeigt, wenn ihm die Laufzeit-Konfiguration fehlt.
//
// ── Der Vorfall, der diese Datei ausgelöst hat ────────────────────────────────────────────────
// Am 2026-09-02, direkt nach dem A-02-Umbau, fehlten auf dev die beiden Supabase-Werte: sie kamen
// bis dahin aus Build-Args und standen nie als Container-Env zur Verfügung. Ergebnis war KEINE
// Fehlermeldung, sondern eine **weisse Seite** — `createClient('', '')` wirft „supabaseUrl is
// required", der Wurf lief durch React durch, und was ankam war ein leeres `<div id="root">`.
//
// Für den, der davorsitzt, sieht das aus wie „das CMS ist kaputt". Tatsächlich fehlten zwei
// Zeilen in einer Compose-Datei. Zwischen diesen beiden Diagnosen liegt eine halbe Stunde
// Suchen — und genau die soll diese Seite sparen.
//
// ⚠️ Bewusst OHNE Übersetzungsschicht, ohne Tailwind-Klassen aus dem Design-System und ohne
// Zustands-Kontexte: diese Seite muss auch dann noch erscheinen, wenn das halbe Gerüst nicht
// hochkommt. Alles, was sie zusätzlich importiert, ist eine weitere Sache, die vorher scheitern
// kann. Die eingebetteten Stile sind der Preis dafür und hier ausdrücklich richtig.
//
// ⛔ Sie nennt die fehlenden VARIABLENNAMEN, nicht ihre Werte — die Werte sind Kontosache.
import React from 'react';

interface KonfigFehltProps {
  /** Die Namen, die in der Container-Umgebung fehlen — in der Form, in der man sie setzt. */
  fehlend: readonly string[];
}

const kasten: React.CSSProperties = {
  maxWidth: '46rem',
  margin: '4rem auto',
  padding: '2rem',
  fontFamily: 'system-ui, -apple-system, sans-serif',
  lineHeight: 1.6,
  color: '#1f2937',
  border: '1px solid #e5e7eb',
  borderRadius: '0.5rem',
  background: '#fff',
};

export const KonfigFehlt: React.FC<KonfigFehltProps> = ({ fehlend }) => (
  <div style={kasten} role="alert">
    <h1 style={{ fontSize: '1.5rem', margin: '0 0 0.5rem' }}>Das CMS ist nicht konfiguriert</h1>
    <p style={{ margin: '0 0 1.5rem', color: '#4b5563' }}>
      Die Oberfläche startet nicht, weil der Anmelde-Dienst keine Adresse hat. Das ist ein
      Konfigurations- und kein Programmfehler — es fehlt nichts an den Daten.
    </p>

    <p style={{ margin: '0 0 0.5rem', fontWeight: 600 }}>
      Diese {fehlend.length === 1 ? 'Variable fehlt' : 'Variablen fehlen'} in der Umgebung des
      Containers:
    </p>
    <ul style={{ margin: '0 0 1.5rem', paddingLeft: '1.25rem' }}>
      {fehlend.map((name) => (
        <li key={name}>
          <code style={{ background: '#f3f4f6', padding: '0.1rem 0.35rem', borderRadius: '0.25rem' }}>
            {name}
          </code>
        </li>
      ))}
    </ul>

    <p style={{ margin: 0, color: '#4b5563', fontSize: '0.925rem' }}>
      Gesetzt werden sie in der Compose des Stacks; beim Start schreibt
      <code style={{ margin: '0 0.3rem' }}>docker/40-laufzeit-konfig.sh</code>
      daraus <code>api-config.runtime.js</code>. Lokal gehören sie in <code>.env</code>. Zum
      Nachsehen, was der laufende Container ausliefert:
    </p>
    <pre
      style={{
        margin: '0.75rem 0 0',
        padding: '0.75rem',
        background: '#f9fafb',
        border: '1px solid #e5e7eb',
        borderRadius: '0.375rem',
        overflowX: 'auto',
        fontSize: '0.875rem',
      }}
    >
      curl https://&lt;cms&gt;/api-config.runtime.js
    </pre>
  </div>
);
